import React, { useCallback, useEffect, useState } from 'react';
import { errorText, mutedText } from './styles';

interface State<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Trigger a re-fetch of the loader. Useful after a mutation. */
  refetch: () => void;
}

export function useViewData<T>(
  loader: (id: string) => Promise<T>,
  campaignId: string,
): State<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setLoading(true);
    setError(null);
    loader(campaignId)
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [loader, campaignId, tick]);

  return { data, loading, error, refetch };
}

interface ViewDataBoundaryProps<T> {
  loader: (id: string) => Promise<T>;
  campaignId: string;
  loadingLabel?: string;
  render: (data: T, refetch: () => void) => React.ReactNode;
}

/**
 * Loads view data for a campaign and renders the loading/error/empty states
 * uniformly so each view component only owns its happy-path layout.
 */
export function ViewDataBoundary<T>({ loader, campaignId, loadingLabel, render }: ViewDataBoundaryProps<T>) {
  const { data, loading, error, refetch } = useViewData(loader, campaignId);
  if (loading) return <p style={mutedText}>{loadingLabel ?? 'Loading…'}</p>;
  if (error) return <p style={errorText}>{error}</p>;
  if (!data) return null;
  return <>{render(data, refetch)}</>;
}
